import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type CreateContractRequest,
  type CreateSupplierInvoiceRequest,
  type UploadPropertyFileRequest,
} from '@/shared/api';
import { prepareForUpload } from './photoUploadPolicy';
import { akteErrorMessage } from './akteErrorMessages';
import type { TopFolder } from './akteFolderMeta';

interface GenericState {
  open: boolean;
  meta: TopFolder | null;
  files: File[];
  /** Ziel-Ordner (Custom-Ordner), wenn der Upload aus einem manuellen Ordner startet. */
  targetFolder?: string;
}
interface FilesState {
  open: boolean;
  files: File[];
}

/**
 * Kapselt das Upload-Routing der Akte: direkter Parallel-Upload fuer
 * metadatenfreie Kategorien, Modal-Routing fuer Vertraege/Rechnungen/Belege.
 */
export function useAkteUploads(propertyId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const [generic, setGeneric] = useState<GenericState>({ open: false, meta: null, files: [] });
  const [contracts, setContracts] = useState<FilesState>({ open: false, files: [] });
  const [invoices, setInvoices] = useState<FilesState>({ open: false, files: [] });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
    void qc.invalidateQueries({ queryKey: ['contracts', propertyId] });
    void qc.invalidateQueries({ queryKey: ['supplierInvoices', propertyId] });
  }

  async function directUpload(meta: TopFolder, files: File[], targetFolder?: string) {
    const meta0: UploadPropertyFileRequest = {
      kind: meta.defaultKind,
      counterparty: null,
      amount: null,
      currency: null,
      issuedOn: null,
      validFrom: null,
      validTo: null,
      description: null,
      relatedFileId: null,
      targetFolder: targetFolder ?? null,
    };
    const results = await Promise.allSettled(
      files.map(async (f) =>
        api.propertyFiles.upload(propertyId, await prepareForUpload(f, meta0.kind), meta0),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    invalidate();
    if (ok === files.length) {
      toast.success(`${ok} ${ok === 1 ? 'Datei' : 'Dateien'} hochgeladen`);
    } else if (ok > 0) {
      toast.warning(`${ok} von ${files.length} hochgeladen — Rest fehlgeschlagen`);
    } else {
      toast.error('Upload fehlgeschlagen');
    }
  }

  /**
   * Zentrale Weiche: aufgerufen von Drop (mit files) und Upload-Klick (files=[]).
   * `targetFolder` (Custom-Ordner) wird nur für die generischen Kinds durchgereicht — Verträge/
   * Rechnungen haben ihre eigene Domain-Ablage.
   */
  function route(meta: TopFolder, files: File[], targetFolder?: string) {
    switch (meta.upload) {
      case 'contracts':
        return setContracts({ open: true, files });
      case 'invoices':
        return setInvoices({ open: true, files });
      case 'generic':
        return setGeneric({ open: true, meta, files, targetFolder });
      case 'direct':
        if (files.length > 0) return void directUpload(meta, files, targetFolder);
        return setGeneric({ open: true, meta, files: [], targetFolder });
    }
  }

  // Fehler in eine klare DE-Meldung übersetzen (z.B. nicht unterstütztes Format) statt eines
  // rohen „Request failed with status code 400" — die Zeile im Modal zeigt genau diesen Text.
  const uploadFile = async (file: File, metadata: UploadPropertyFileRequest) => {
    try {
      await api.propertyFiles.upload(
        propertyId,
        await prepareForUpload(file, metadata.kind),
        metadata,
      );
      invalidate();
    } catch (err) {
      throw new Error(akteErrorMessage(await toApiError(err)));
    }
  };

  async function uploadContract(file: File, metadata: CreateContractRequest): Promise<void> {
    await api.contracts.create(propertyId, file, metadata);
    invalidate();
  }
  async function uploadInvoice(file: File, metadata: CreateSupplierInvoiceRequest): Promise<void> {
    await api.supplierInvoices.create(propertyId, file, metadata);
    invalidate();
  }

  function deleteFile(fileId: string) {
    api.propertyFiles
      .delete(propertyId, fileId)
      .then(() => {
        toast.success('Datei gelöscht');
        invalidate();
      })
      .catch(async (err: unknown) => {
        const e = await toApiError(err);
        toast.error(e.detail ?? e.title);
      });
  }

  return {
    generic,
    contracts,
    invoices,
    setGeneric,
    setContracts,
    setInvoices,
    route,
    uploadFile,
    uploadContract,
    uploadInvoice,
    deleteFile,
  };
}
